import { asArray, asObject, text, uniqueStrings } from './model-utils.js';
import { CHAPTER_DEFINITIONS, createAssetItem, createChapter, createItem, isCompletedStage } from './document-model.js';

const SOURCE = stage => ({ stage });
const fact = (label, value) => ({ label, value: text(value) });
const facts = rows => rows.filter(row => row.value);

const TABLE_COLUMNS = {
  materials: [['filename', '资料名称'], ['mimeType', '类型'], ['scope', '解析状态']],
  conditions: [['label', '指标或约束'], ['value', '确认值'], ['source', '来源'], ['evidence', '依据']],
  concepts: [['direction', '方向'], ['name', '方案名称'], ['tagline', '定位'], ['coreIdea', '核心理念'], ['layout', '布局结构']],
  buildings: [['zone', '分区'], ['name', '建筑或功能'], ['function', '主要功能'], ['floors', '层数'], ['floorArea', '建筑面积'], ['footprint', '占地面积'], ['notes', '备注']]
};

const columns = key => TABLE_COLUMNS[key].map(([columnKey, label]) => ({ key: columnKey, label }));

function completedData(context, stageName) {
  const stage = context.stages[stageName];
  return isCompletedStage(stage) ? asObject(stage.data) : null;
}

function selectedConcept(context) {
  const data = completedData(context, 'concept');
  if (!data) return null;
  const selectedId = text(data.selectedConceptId || data.selectedConcept?.id);
  if (!selectedId) return null;
  return asObject(data.selectedConcept?.id === selectedId
    ? data.selectedConcept
    : asArray(data.concepts).find(item => text(item?.id) === selectedId));
}

function buildCover(context) {
  const project = context.project;
  return [createItem('cover:project', 'facts', project.name, {
    facts: facts([
      fact('项目类型', project.type),
      fact('项目地点', project.location),
      fact('当前进度', `${project.progress}%`),
      fact('成果版本', project.updatedAt)
    ])
  }, { source: SOURCE('project') })];
}

function buildOverview(context) {
  const conditionData = completedData(context, 'conditions');
  const summary = text(conditionData?.projectSummary || context.project.description);
  return [
    summary && createItem('overview:summary', 'text', '项目摘要', { text: summary }, { source: SOURCE(conditionData ? 'conditions' : 'project') }),
    createItem('overview:facts', 'facts', '项目基本信息', {
      facts: facts([
        fact('项目名称', context.project.name),
        fact('项目类型', context.project.type),
        fact('项目位置', context.project.location),
        fact('完成度', `${context.project.progress}%`)
      ])
    }, { source: SOURCE('project') })
  ];
}

function buildSources(context) {
  const data = completedData(context, 'materials');
  if (!data) return [];
  const parsedIds = new Set(asArray(data.materialIds).map(text).filter(Boolean));
  const rows = context.materials.filter(item => parsedIds.has(text(item.id))).map(item => ({
    filename: text(item.filename, '未命名资料'),
    mimeType: text(item.mime_type || item.mimeType, '未知'),
    scope: item.extraction_status === 'done' || item.extracted_text ? '已纳入解析' : '已保存'
  }));
  return [
    rows.length && createItem('sources:materials', 'table', '资料清单', { columns: columns('materials'), rows }, { source: SOURCE('materials') }),
    createItem('sources:scope', 'facts', '解析范围', {
      facts: facts([
        fact('解析模式', data.analysisMode),
        fact('解析状态', data.analysisStatus),
        fact('纳入资料', `${parsedIds.size} 份`),
        fact('关键图页', `${asArray(data.visualSelectionIds).length} 张`),
        fact('解析时间', data.analyzedAt)
      ])
    }, { source: SOURCE('materials') })
  ];
}

function buildConstraints(context) {
  const data = completedData(context, 'conditions');
  if (!data) return [];
  const rows = asArray(data.conditions).filter(item => item?.userConfirmed === true).map(item => ({
    label: text(item.label || item.key, '项目条件'),
    value: text(item.value, '待确认'),
    source: text(item.source, '用户确认'),
    evidence: text(item.evidence)
  }));
  return rows.length
    ? [createItem('constraints:confirmed', 'table', '已确认指标与约束', { columns: columns('conditions'), rows }, { source: SOURCE('conditions') })]
    : [];
}

function buildSiteAnalysis(context) {
  const data = completedData(context, 'conditions');
  if (!data) return [];
  const site = asObject(data.siteAnalysis);
  const siteFacts = facts([
    fact('地理位置', site.location), fact('周边环境', site.surroundings),
    fact('建设内容', site.construction || site.buildingContent),
    fact('建筑风貌', site.architecture || site.ownerNeeds),
    fact('设计切入点', site.planningIntent || site.designIntent)
  ]);
  const opportunities = uniqueStrings(asArray(data.designOpportunities));
  return [
    siteFacts.length && createItem('site-analysis:context', 'facts', '基地分析', { facts: siteFacts }, { source: SOURCE('conditions') }),
    opportunities.length && createItem('site-analysis:opportunities', 'list', '设计切入点', { entries: opportunities }, { source: SOURCE('conditions') })
  ];
}

function buildRisks(context) {
  const data = completedData(context, 'conditions');
  if (!data) return [];
  const riskEntries = asArray(data.risks).map(item => text(typeof item === 'string' ? item : item?.title || item?.issue));
  const missingEntries = asArray(data.missing).map(item => {
    if (typeof item === 'string') return `待补充：${item}`;
    const titleValue = text(item?.title || item?.issue);
    const detail = text(item?.impact || item?.suggestion);
    return titleValue ? `待补充：${titleValue}${detail ? ` - ${detail}` : ''}` : '';
  });
  const entries = uniqueStrings([...riskEntries, ...missingEntries]);
  return entries.length
    ? [createItem('risks:review', 'list', '风险与待复核事项', { entries }, { source: SOURCE('conditions') })]
    : [];
}

function buildConceptOptions(context) {
  const data = completedData(context, 'concept');
  if (!data) return [];
  const rows = asArray(data.concepts).map((item, index) => ({
    direction: text(item.id, String.fromCharCode(65 + index)),
    name: text(item.name, `方案方向 ${index + 1}`),
    tagline: text(item.tagline),
    coreIdea: text(item.coreIdea),
    layout: text(item.layoutStructure || item.layout)
  }));
  return rows.length
    ? [createItem('concept-options:comparison', 'table', '方案方向对比', { columns: columns('concepts'), rows }, { source: SOURCE('concept') })]
    : [];
}

function buildSelectedConcept(context) {
  const data = completedData(context, 'concept');
  const selected = selectedConcept(context);
  if (!data || !selected?.id) return [];
  const selectedFacts = facts([
    fact('采用方向', selected.id), fact('方案名称', selected.name), fact('一句话定位', selected.tagline),
    fact('核心设计理念', selected.coreIdea), fact('布局结构', selected.layoutStructure || selected.layout),
    fact('功能分区', selected.zoningStrategy), fact('入口与流线', selected.entryFlowStrategy || selected.circulation),
    fact('公共空间与景观', selected.publicLandscapeStrategy)
  ]);
  const assetId = selected.generatedPlanStatus && selected.generatedPlanStatus !== 'completed'
    ? ''
    : selected.generatedPlanAssetId;
  return [
    createItem('selected-concept:facts', 'facts', text(selected.name, '最终采用方案'), { facts: selectedFacts }, { source: SOURCE('concept') }),
    createAssetItem(context.assetIndex, assetId, { title: `${text(selected.name, '采用方案')} · 概念总图`, stage: 'concept' })
  ];
}

function flattenBuildings(data) {
  const direct = asArray(data.buildings);
  if (direct.length) return direct;
  return asArray(data.zones).flatMap(zone => asArray(zone?.buildings).map(building => ({ ...building, zone: zone.name })));
}

function buildMasterplan(context) {
  const data = completedData(context, 'masterplan');
  if (!data) return [];
  const strategy = asObject(data.strategies);
  const strategyFacts = facts([
    fact('功能分区', strategy.zoning), fact('交通组织', strategy.traffic),
    fact('消防策略', strategy.fireSafety), fact('景观系统', strategy.landscape), fact('分期策略', strategy.phasing)
  ]);
  const assetId = text(data.latestAssetId || data.generatedPlanAssetId);
  return [
    data.summary && createItem('masterplan-strategy:summary', 'text', '总图方案摘要', { text: text(data.summary) }, { source: SOURCE('masterplan') }),
    strategyFacts.length && createItem('masterplan-strategy:strategies', 'facts', '总图设计策略', { facts: strategyFacts }, { source: SOURCE('masterplan') }),
    createAssetItem(context.assetIndex, assetId, { title: '采用的总图成果', stage: 'masterplan' })
  ];
}

function buildBuildings(context) {
  const data = completedData(context, 'masterplan');
  if (!data) return [];
  const rows = flattenBuildings(data).map(item => ({
    zone: text(item.zone), name: text(item.name), function: text(item.function || item.type),
    floors: item.floors ?? '', floorArea: item.floorArea ?? item.area ?? '', footprint: item.footprint ?? '', notes: text(item.notes)
  }));
  return rows.length
    ? [createItem('building-program:list', 'table', '建筑与功能清单', { columns: columns('buildings'), rows }, { source: SOURCE('masterplan') })]
    : [];
}

function buildMetrics(context) {
  const data = completedData(context, 'masterplan');
  if (!data) return [];
  const metric = asObject(data.metrics);
  const metricFacts = facts([
    fact('功能分区', metric.zones), fact('建筑数量', metric.buildings), fact('总建筑面积', metric.totalFloorArea),
    fact('总占地面积', metric.footprint), fact('容积率', metric.far), fact('建筑密度', metric.density)
  ]);
  return metricFacts.length
    ? [createItem('metrics:masterplan', 'facts', '主要规模指标', { facts: metricFacts }, { source: SOURCE('masterplan') })]
    : [];
}

function buildVisualResults(context) {
  const data = completedData(context, 'visual');
  if (!data) return [];
  const explicitIds = asArray(data.selectedAssetIds).length
    ? asArray(data.selectedAssetIds)
    : asArray(data.savedAssetIds).length ? asArray(data.savedAssetIds) : asArray(data.latestAssetId);
  return uniqueStrings(explicitIds).map(assetId => createAssetItem(context.assetIndex, assetId, {
    title: context.assetIndex.get(assetId)?.title || text(data.type, '效果表达成果'),
    caption: [data.templateName, data.type, data.outputRatio, data.outputQuality].map(text).filter(Boolean).join(' · '),
    stage: 'visual'
  })).filter(Boolean);
}

function buildConclusion(context) {
  const conceptData = completedData(context, 'concept');
  const masterData = completedData(context, 'masterplan');
  const selected = selectedConcept(context);
  const entries = uniqueStrings([
    conceptData?.recommendation,
    selected?.deepeningFocus,
    ...asArray(selected?.risks),
    ...asArray(masterData?.warnings),
    ...asArray(masterData?.assumptions)
  ]);
  return entries.length
    ? [createItem('conclusion:next-steps', 'list', '后续深化建议', { entries }, { source: SOURCE('derived') })]
    : [];
}

const BUILDERS = [
  buildCover, buildOverview, buildSources, buildConstraints, buildSiteAnalysis, buildRisks,
  buildConceptOptions, buildSelectedConcept, buildMasterplan, buildBuildings, buildMetrics,
  buildVisualResults, buildConclusion
];

/** @param {Record<string, any>} context */
export function buildChapters(context) {
  return CHAPTER_DEFINITIONS.map((definition, index) => createChapter(definition, BUILDERS[index](context)));
}
